import React, { Component } from 'react';
import { View, TouchableWithoutFeedback, Text, StatusBar, BackHandler, Animated } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { WebView } from 'react-native-webview';
import withNavigation from '../../utils/WithNavigation';
import Toast from 'react-native-easy-toast';
import Utility from '../../utils/Utility';
import Config from '../../libraries/ReactNativeConfig';
import colors from '../../theme/Colors';
import size from '../../theme/Fonts';
import { AnalyticsManager, EventType, EventParameterKey } from '../../analytics';
import { CartStyles } from '../cart/styles';
import RemoteConfig from '../../utils/RemoteConfig';
import { REMOTE_CONFIG_KEYS } from '../../config/Constants';
import AppConfig from '../../config/AppConfig';
import COMMON_INJECTED_JAVASCRIPT from '../../injected/Common';
import { hideSmartBanner, showSmartBanner } from '../../utils/SmartBannerUtility';
import { getStoreRef } from '../../store/StoreUtility';
import { isSurveyFormSubmitted, routineSurveyFormSubmitted } from '../../actions/ActionTypes';
import StaticNavigationHeader from './StaticNavigationHeader';
import { refreshMyAttributes } from '../../actions/FacialAnalysisActions';
import { SCREEN_CONSTANTS } from '../../config/ScreenConstants';

const { styles, ids } = StyleSheet.create({
  container: { flex: 1 },
  webViewContainer: { flex: 1.7 },
  webView: { flex: 1 },
  buttonLayout: {
    width: Utility.getScreenWidth(),
    flex: 0.3,
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: 'white',
    '@media (min-width: 800px)': {
      width: '100%',
      padding: 0,
      flex: 0.2,
    },
  },
  buttonContainer: {
    height: 46,
    backgroundColor: Config.THEME_COLOR || colors.green,
    width: Utility.getButtonWidth(),
    borderRadius: 8,
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: colors.white,
    fontSize: size.h1,
    fontFamily: 'Roboto-Medium',
  },
  progressBarContainer: {
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1,
  },
  barBackground: {
    height: 4,
    backgroundColor: colors.background,
    borderRadius: 10,
    width: 150,
    alignSelf: 'center',
    position: 'relative', // Ensure it's not interfering with the bar
  },
  bar: {
    height: 4,
    backgroundColor: Config.THEME_COLOR || colors.black, // Bright green for visibility
    borderRadius: 10,
    position: 'absolute',
    zIndex: 2,
    left: 0,
  },
});

class TypeFormWebUrlView extends Component {
  static getWebviewUrl(url) {
    return url || Config.HTTPS_DOMAIN_NAME;
  }

  constructor(props) {
    super(props);
    const { route: { params = {} } = {} } = props;

    this.webViewRef = React.createRef();
    this.state = this.getInitialState(params);
    this.config = this.initializeConfig(params);
  }

  getInitialState(params) {
    return {
      title: params.title || '',
      showBottomButton: params.showBottomButton || false,
      fromOnboarding: params.fromOnboarding || false,
      showPlaceholder: true, // Always start with progress bar visible
      progress: new Animated.Value(0),
      isFormSubmitted: false,
      paused: false,
    };
  }

  initializeConfig(params) {
    return {
      disableHttpErrorReturn: params.disableHttpErrorReturn || false,
      replaceScreen: params.replaceScreen,
      previousScreen: params.previousScreen,
      injectedJavaScript: `${COMMON_INJECTED_JAVASCRIPT} ${params.customInjectedJavascript || ''}`,
      slug: params.slug || '',
      furtherAction: params.furtherAction || (() => {}),
      destination: params.newDestination || params.destination,
      title: params.title || '',
      shouldWaitForAttributes: params.shouldWaitForAttributes || '',
      shouldGoBack: params.shouldGoBack || false,
    };
  }

  componentDidMount() {
    this.backHandler = BackHandler.addEventListener('hardwareBackPress', this.handleBackPress);
    this.setupNavigationListeners();
    this.startProgressAnimation();
  }

  componentWillUnmount() {
    this.backHandler.remove();
    this.unsubscribeFocus();
    this.unsubscribeBlur();
  }

  setupNavigationListeners() {
    const { navigation } = this.props;
    this.unsubscribeFocus = navigation.addListener('focus', () => {
      hideSmartBanner();
      this.setState({ paused: false });
    });
    this.unsubscribeBlur = navigation.addListener('blur', () => {
      showSmartBanner();
      this.setState({ paused: true });
    });
  }

  startProgressAnimation() {
    const { progress } = this.state;
    Animated.timing(progress, {
      toValue: 150,
      duration: 3000,
      useNativeDriver: false,
    }).start(() => {});
  }

  handleNavigation = () => {
    const { navigation = {} } = this.props;
    const { isFormSubmitted } = this.state;
    if (isFormSubmitted) {
      this.config.furtherAction();
      return;
    }

    const routes = navigation.getState?.()?.routes || [];
    routes.length < 2 ? navigation.replace?.('TabNavigator') : navigation.goBack?.();
  };

  handleContinue = () => {
    const { fromOnboarding, title, isFormSubmitted } = this.state;
    if (isFormSubmitted) {
      this.config.furtherAction();
      return;
    }

    if (fromOnboarding) {
      AnalyticsManager.logEvent(EventType.onboardingEvent.ONBOARDING_LINK_CONTINUE, {
        [EventParameterKey.LINK_NAME]: title,
        [EventParameterKey.METHOD]: 'Button Press',
      });
    }
    this.handleNavigation();
  };

  handleBackPress = () => {
    const { fromOnboarding, title, isFormSubmitted } = this.state;
    if (isFormSubmitted) {
      this.config.furtherAction();
      return true;
    }

    if (fromOnboarding) {
      AnalyticsManager.logEvent(EventType.onboardingEvent.ONBOARDING_LINK_CONTINUE, {
        [EventParameterKey.LINK_NAME]: title,
        [EventParameterKey.METHOD]: 'Back Press',
      });
    }
    this.handleNavigation();
    return true;
  };

  handleMessage = (message) => {
    const { data } = message.nativeEvent;
    const { navigation } = this.props;

    try {
      const parsedData = Utility.jsonParser(decodeURIComponent(decodeURIComponent(data)));
      const { type, showPlaceholder, event, success } = parsedData;
      this.setState({ showPlaceholder });

      if (success !== undefined) {
        this.handleNavigation();
        return;
      }

      if (event === 'go_to_home_page') {
        navigation.navigate('Feed');
      }
      AnalyticsManager.logEvent(event, parsedData.params);
    } catch (error) {
      console.tron.log('Message parsing error:', error);
    }
  };

  navigateToProgressBar = () => {
    const { navigation } = this.props;
    navigation.replace(SCREEN_CONSTANTS.ATTRIBUTE_LOADER, {
      config: this.config,
    });
  };

  onNavigationStateChange = (navState = {}) => {
    const { url = '', loading = false } = navState;
    const { navigation = {} } = this.props;

    this.handleLoadingState(loading);

    if (!url.includes('routines')) {
      return true;
    }

    return this.handleRoutineSubmission();
  };

  handleLoadingState = (loading) => {
    if (!loading) {
      this.setState({ showPlaceholder: false });
    }
  };

  handleRoutineSubmission = () => {
    const { navigation } = this.props;
    this.updateSubmissionState();
    this.notifySubmissionSuccess();

    const { previousScreen, shouldWaitForAttributes, shouldGoBack } = this.config;

    if (shouldWaitForAttributes) {
      this.navigateToProgressBar();
    } else {
      this.handleDefaultNavigation(navigation, shouldGoBack);
    }

    return false;
  };

  updateSubmissionState = () => {
    const store = getStoreRef();
    const { routine_id: routineId, previousScreen } = this.props.route.params;
    const { routineSurveyFormSubmitted: previousFilledAssessment } = store.getState()?.UserAccountInfo;
    this.setState({ isFormSubmitted: true, showPlaceholder: false });
    if (previousScreen === SCREEN_CONSTANTS.PRE_ROUTINE_DETAILS) {
      const updatedFilledAssessment = {
        ...previousFilledAssessment,
        [routineId]: true,
      };
      store.dispatch(routineSurveyFormSubmitted(updatedFilledAssessment));
    } else {
      store.dispatch(isSurveyFormSubmitted(true));
    }
  };

  notifySubmissionSuccess = () => {
    this.toast?.show('Form submitted successfully!', 3000);
  };

  handleDefaultNavigation = (navigation, shouldGoBack) => {
    if (shouldGoBack) {
      navigation.goBack();
    }
    this.config.furtherAction();
    getStoreRef().dispatch(refreshMyAttributes());
  };

  onLoadEnd = () => {
    this.injectJavaScriptOnLoadEnd();
  };

  onError = (syntheticEvent) => {
    this.setState({ showPlaceholder: false }); // Hide progress bar on error
  };

  injectJavaScriptOnLoadEnd = () => {
    const { route: { params: { injectJavaScriptOnLoadEnd = false } = {} } = {} } = this.props;
    if (injectJavaScriptOnLoadEnd) {
      this.webViewRef.current.injectJavaScript(this.config.injectedJavaScript);
    }
  };

  renderButtonLayout = () => (
    <TouchableWithoutFeedback onPress={this.handleContinue}>
      <View style={styles.buttonLayout} dataSet={{ media: ids.buttonLayout }}>
        <View style={styles.buttonContainer} dataSet={{ media: ids.buttonContainer }}>
          <Text style={styles.buttonText}>Continue</Text>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );

  renderProgressBar = () => {
    const { progress } = this.state;
    return (
      <View style={styles.progressBarContainer}>
        <View>
          <View style={styles.barBackground} />
          <Animated.View style={[styles.bar, { width: progress }]} />
        </View>
      </View>
    );
  };

  setToastRef = (ref) => (this.toast = ref);

  render() {
    const { showBottomButton, showPlaceholder, title } = this.state;
    const { route: { params: { showSelfieUpdater = false } = {} } = {} } = this.props;
    const webviewUrl = TypeFormWebUrlView.getWebviewUrl(this.config.destination);
    const redirectingText = RemoteConfig.getValue(REMOTE_CONFIG_KEYS.redirect_text);

    return (
      <View style={styles.container}>
        <StatusBar barStyle='dark-content' />
        <StaticNavigationHeader
          title={title}
          onBackPress={this.handleNavigation}
          showSelfieUpdater={showSelfieUpdater}
        />
        <View style={styles.webViewContainer}>
          <WebView
            ref={this.webViewRef}
            style={styles.webView}
            source={{ uri: webviewUrl }}
            onHttpError={(syntheticEvent) => {
              if (this.config.disableHttpErrorReturn) return;
              this.toast.show(redirectingText, 3000);
              setTimeout(this.handleNavigation, 3000);
            }}
            injectedJavaScript={this.config.injectedJavaScript}
            onMessage={this.handleMessage}
            onLoadEnd={this.onLoadEnd}
            onError={this.onError}
            androidLayerType='hardware'
            domStorageEnabled
            scalesPageToFit
            cacheMode='LOAD_DEFAULT'
            onNavigationStateChange={this.onNavigationStateChange}
          />
          {showPlaceholder && this.renderProgressBar()}
        </View>
        {showBottomButton && this.renderButtonLayout()}
        <Toast style={CartStyles.toast} ref={this.setToastRef} />
      </View>
    );
  }
}

export default withNavigation(TypeFormWebUrlView);
